import { useEffect, useState } from "react";
import { ArrowsClockwise, PaperPlaneTilt, Question } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { JOB_STATUS_LABELS, JOB_STATUS_COLORS } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function RevisionJobs() {
  const [jobs, setJobs] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = () => api.get("/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  useEffect(() => { load(); }, []);

  const patch = async (id, body, msg) => {
    try { await api.put(`/jobs/${id}`, body); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!jobs) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading revision jobs…</div>;

  const active = jobs.filter((j) => j.status === "admin_review");
  const resolved = jobs.filter((j) => j.status !== "admin_review" && (j.query_text || j.query_response));
  const delivered = jobs.filter((j) => j.status === "sent_to_client");

  const d = (id, k) => drafts[id]?.[k] ?? "";
  const setD = (id, k) => (e) => setDrafts({ ...drafts, [id]: { ...(drafts[id] || {}), [k]: e.target.value } });

  return (
    <div className="space-y-6" data-testid="revision-jobs-page">
      <PageHeader overline="Valuation Jobs" title="Revision / Query Jobs" subtitle="Reports returned by the bank with queries or objections — track the back-and-forth to re-submission" />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Open queries" value={active.length} tone={active.length ? "text-rose-600" : "text-emerald-600"} testid="stat-open-queries" />
        <Stat label="Resolved / re-submitted" value={resolved.length} tone="text-emerald-600" testid="stat-resolved" />
        <Stat label="Delivered (can raise query)" value={delivered.length} testid="stat-delivered" />
      </div>

      {active.length === 0 ? (
        <EmptyState icon={Question}>No open queries. If a bank returns a report, mark that job as <strong>Query / Revision</strong> and log the query here.</EmptyState>
      ) : (
        <div className="space-y-3">
          {active.map((j) => (
            <Card key={j.id} className="p-5 border-border border-l-4 border-l-rose-400" data-testid={`rev-card-${j.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-lg text-primary">{j.applicant_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{j.reference} · {j.bank_name}{j.bank_reference_no ? ` · Bank ref ${j.bank_reference_no}` : ""}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded border shrink-0 ${JOB_STATUS_COLORS[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
              </div>

              <div className="grid md:grid-cols-2 gap-3 mt-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Query raised by bank</Label>
                  <Textarea rows={3} value={d(j.id, "query_text") || j.query_text || ""} onChange={setD(j.id, "query_text")} placeholder="What did the bank object to?" data-testid={`rev-query-${j.id}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Our response</Label>
                  <Textarea rows={3} value={d(j.id, "query_response") || j.query_response || ""} onChange={setD(j.id, "query_response")} placeholder="How was it addressed?" data-testid={`rev-response-${j.id}`} />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" variant="outline" className="rounded-full"
                  onClick={() => patch(j.id, { query_text: d(j.id, "query_text") || j.query_text || "", query_response: d(j.id, "query_response") || j.query_response || "" }, "Query log saved")}
                  data-testid={`rev-save-${j.id}`}>Save Query Log</Button>
                <Button size="sm" className="rounded-full bg-primary hover:bg-[#113A64]"
                  onClick={() => patch(j.id, { status: "admin_review", query_response: d(j.id, "query_response") || j.query_response || "" }, "Sent back to QC before re-submission")}
                  data-testid={`rev-to-qc-${j.id}`}>Send to QC</Button>
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => patch(j.id, { status: "sent_to_client", query_response: d(j.id, "query_response") || j.query_response || "" }, "Marked re-submitted")}
                  data-testid={`rev-resubmit-${j.id}`}><PaperPlaneTilt size={15} className="mr-1" /> Mark Re-submitted</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {delivered.length > 0 && (
        <Card className="p-5 border-border">
          <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-3">Delivered reports — flag one if the bank raises a query</div>
          <div className="space-y-1.5">
            {delivered.map((j) => (
              <div key={j.id} className="flex flex-wrap items-center justify-between gap-2 p-2 rounded bg-muted text-sm" data-testid={`rev-delivered-${j.id}`}>
                <span className="text-primary truncate">{j.applicant_name} · {j.bank_name} <span className="font-mono text-xs text-muted-foreground">{j.reference}</span></span>
                <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs text-rose-600 hover:bg-rose-50"
                  onClick={() => patch(j.id, { status: "admin_review" }, "Moved to Query / Revision")}
                  data-testid={`rev-flag-${j.id}`}>
                  <ArrowsClockwise size={13} className="mr-1" /> Raise query
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {resolved.length > 0 && (
        <Card className="p-5 border-border">
          <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-3">Resolved query history</div>
          <div className="space-y-2">
            {resolved.map((j) => (
              <div key={j.id} className="p-3 rounded-md bg-muted border border-border" data-testid={`rev-history-${j.id}`}>
                <div className="text-sm font-medium text-primary">{j.applicant_name} <span className="font-mono text-xs text-muted-foreground">{j.reference}</span></div>
                {j.query_text && <div className="text-xs text-muted-foreground mt-1"><strong>Query:</strong> {j.query_text}</div>}
                {j.query_response && <div className="text-xs text-muted-foreground mt-0.5"><strong>Response:</strong> {j.query_response}</div>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
